import { useState } from 'react';
import { 
  Phone, 
  Mail, 
  Clock,
  Send,
  CheckCircle2,
  Loader2,
  MessageSquare,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    callback_requested: false,
    preferred_time: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, callback_requested: checked }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, preferred_time: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        callback_requested: false,
        preferred_time: ''
      });
    } catch (err) {
      setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main id="main-content" data-testid="kontakt-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-[#23D3D3]/5 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
              <MessageSquare className="w-4 h-4 text-[#1A52D8]" />
              <span className="text-sm font-medium text-[#1A52D8]">Kontakt</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1e293b] leading-tight mb-6">
              Wir sind für Sie da
            </h1>
            <p className="text-lg text-[#64748b] leading-relaxed">
              Haben Sie Fragen zur ambulanten Pflege? Wir beraten Sie gerne – 
              kostenlos und unverbindlich.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="relative -mt-8 z-20 mb-16">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-[#1A52D8] to-[#23D3D3] rounded-2xl p-6 md:p-8 shadow-xl shadow-[#1A52D8]/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-white/80 text-sm mb-1">Rufen Sie uns direkt an</p>
                <p className="text-white text-xl font-bold">Sofortige Beratung</p>
              </div>
              <a href="tel:+4915146173685" data-testid="contact-phone-banner">
                <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-8 rounded-full text-lg font-semibold shadow-lg">
                  <Phone className="w-5 h-5" />
                  Jetzt Anrufen
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24" data-testid="contact-main">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form - 3 columns */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#e2e8f0] shadow-sm">
                <h2 className="text-2xl font-bold text-[#1e293b] mb-2">
                  Nachricht senden
                </h2>
                <p className="text-[#64748b] mb-8">
                  Füllen Sie das Formular aus und wir melden uns schnellstmöglich.
                </p>
                
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="form-success">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#23D3D3] to-[#98E72B] flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#1e293b] mb-3">
                      Vielen Dank!
                    </h3>
                    <p className="text-[#64748b] mb-8 max-w-md mx-auto">
                      Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns schnellstmöglich bei Ihnen.
                    </p>
                    <Button 
                      className="bg-[#1A52D8]/10 text-[#1A52D8] hover:bg-[#1A52D8]/20 h-12 px-6 rounded-full font-semibold"
                      onClick={() => setIsSubmitted(false)}
                    >
                      Neue Nachricht senden
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#1e293b] font-medium mb-2 block">
                          Ihr Name <span className="text-[#ef4444]">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="h-12 rounded-xl border-[#e2e8f0] focus:border-[#1A52D8] focus:ring-[#1A52D8]/20"
                          placeholder="Max Mustermann"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-[#1e293b] font-medium mb-2 block">
                          Telefon
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="h-12 rounded-xl border-[#e2e8f0] focus:border-[#1A52D8] focus:ring-[#1A52D8]/20"
                          placeholder="+49 123 456789"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#1e293b] font-medium mb-2 block">
                        E-Mail <span className="text-[#ef4444]">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="h-12 rounded-xl border-[#e2e8f0] focus:border-[#1A52D8] focus:ring-[#1A52D8]/20"
                        placeholder="ihre@email.de"
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#1e293b] font-medium mb-2 block">
                        Ihre Nachricht <span className="text-[#ef4444]">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="min-h-[140px] rounded-xl border-[#e2e8f0] focus:border-[#1A52D8] focus:ring-[#1A52D8]/20 resize-none"
                        placeholder="Wie können wir Ihnen helfen?"
                        data-testid="input-message"
                      />
                    </div>

                    {/* Callback Option */}
                    <div className="bg-[#f8fafb] rounded-xl p-5 border border-[#e2e8f0]">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="callback"
                          checked={formData.callback_requested}
                          onCheckedChange={handleCheckboxChange}
                          className="mt-0.5 data-[state=checked]:bg-[#1A52D8] data-[state=checked]:border-[#1A52D8]"
                          data-testid="checkbox-callback"
                        />
                        <div className="flex-1">
                          <Label htmlFor="callback" className="text-[#1e293b] font-medium cursor-pointer">
                            Ich möchte zurückgerufen werden
                          </Label>
                          
                          {formData.callback_requested && (
                            <div className="mt-4">
                              <Label className="text-[#64748b] text-sm mb-2 block">
                                Wann erreichen wir Sie am besten?
                              </Label>
                              <Select value={formData.preferred_time} onValueChange={handleSelectChange}>
                                <SelectTrigger className="h-12 rounded-xl border-[#e2e8f0]" data-testid="select-time">
                                  <SelectValue placeholder="Zeit auswählen" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="morning">Vormittags (8-12 Uhr)</SelectItem>
                                  <SelectItem value="afternoon">Nachmittags (12-17 Uhr)</SelectItem>
                                  <SelectItem value="evening">Abends (17-19 Uhr)</SelectItem>
                                  <SelectItem value="anytime">Jederzeit</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="bg-[#fef2f2] text-[#dc2626] p-4 rounded-xl text-sm" data-testid="form-error">
                        {error}
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full bg-[#1A52D8] text-white hover:bg-[#1545b8] h-14 rounded-xl text-lg font-semibold shadow-lg shadow-[#1A52D8]/25 hover:shadow-xl transition-all duration-300"
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Nachricht senden
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Cards */}
              <div className="bg-white rounded-2xl p-6 border border-[#e2e8f0] shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e293b] mb-1">Telefon</h3>
                    <a href="tel:+4915146173685" className="text-[#1A52D8] font-medium hover:underline">
                      Jetzt Anrufen
                    </a>
                    <p className="text-sm text-[#64748b] mt-1">Rund um die Uhr erreichbar</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-[#e2e8f0] shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#23D3D3] to-[#98E72B] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e293b] mb-1">E-Mail</h3>
                    <a href="mailto:info@pflegeteam-itzehoe.de" className="text-[#1A52D8] font-medium hover:underline break-all">
                      info@pflegeteam-itzehoe.de
                    </a>
                    <p className="text-sm text-[#64748b] mt-1">Wir antworten schnellstmöglich</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-[#e2e8f0] shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1A52D8]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#1A52D8]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e293b] mb-1">Erreichbarkeit</h3>
                    <p className="text-[#1e293b] font-medium">24/7 Notfallbereitschaft</p>
                    <p className="text-sm text-[#64748b] mt-1">Büro: Mo-Fr 8:00-17:00 Uhr</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl overflow-hidden border border-[#e2e8f0] shadow-sm" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d37955.36858566725!2d9.489883049999999!3d53.9253611!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b3fa8a831c8e71%3A0x4263df27bd63b30!2sItzehoe!5e0!3m2!1sde!2sde!4v1700000000000!5m2!1sde!2sde"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Standort Itzehoe"
                />
              </div>

              {/* Service Area */}
              <div className="bg-gradient-to-br from-[#f8fafb] to-white rounded-2xl p-6 border border-[#e2e8f0]">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-[#23D3D3]" />
                  <h3 className="font-semibold text-[#1e293b]">Unser Einzugsgebiet</h3>
                </div>
                <p className="text-sm text-[#64748b]">
                  Itzehoe, Krempe, Wilster, Glückstadt, Kellinghusen, Hohenlockstedt und Umgebung.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
